import { Injectable, Signal, computed, signal } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import to from 'await-to-js';
import type { EpubCheckResult } from 'epubcheck-standalone';
import type { ReportMessage } from 'epubcheck-standalone/formatters';
import { ServerConnectionService } from '../server-connection.service';
import { CoreModule } from '../../core.module';
import {
	ILeadMagnet,
	ILeadMagnetValidation,
	ILeadMagnetValidationIssue,
	ISiteAdvert,
} from '../../../types/datatypes';
import { SitesService } from './sites.service';
import { IPopulatedFeature } from '../../../types/populated';

export type LeadMagnetPayload = {
	leadMagnet: Partial<ILeadMagnet>;
	feature: {
		pubDate: string;
		_siteAdvert: string;
		purchaserInfo: {
			email: string;
			firstName: string;
			lastName: string;
		};
	};
}

// Any feature-like object we can check for a lead magnet site and key a lookup by.
export type LeadMagnetFeature = {
	_id: string;
	_site: string | { _id: string };
};

// Shape consumed by Features Step 5 to render a lead magnet in place of the usual product data.
export type LeadMagnetStep5 = {
	funding: 'SHARED' | 'STANDARD';
	title: string;
	author: string;
	cover: string;
	description: string;
	headline: string;
	tagline: string;
	details: string;
};

// Shape consumed by cart items to fall back to lead magnet art/copy when there's no book cover.
export type LeadMagnetCart = {
	image: string;
	title: string;
};

export type LeadMagnetResponse = Omit<ILeadMagnet, '_feature'> & { _feature: IPopulatedFeature };

type EpubIssueCategory = {
	title: string;
	description: string;
	/**
	 * Exact EPUBCheck message ids, e.g. 'RSC-007'
	 */
	ids?: string[];
	/**
	 * Non-specific prefixes to match e.g. 'OPF' matches any OPF-xyz issue
	 */
	prefixes?: string[];
	/**
	 * Whether to display the raw/original messages, only used for the fallback (non mapped errors)
	 */
	showMessages?: boolean;
};

const EPUB_ENGINE_URL = '/assets/epubcheck/epubcheck-engine.js';

const EPUB_VALIDATION_TIMEOUT_MS = 120_000;

/**
 * Groups EPUBCheck message ids into user-facing issues, loosely inspired by Apple Books' asset validation.
 * Reference for the ids: https://github.com/w3c/epubcheck/blob/main/src/main/resources/com/adobe/epubcheck/messages/MessageBundle.properties
 */
const EPUB_ISSUE_CATEGORIES: EpubIssueCategory[] = [
	{
		title: 'The file is damaged or isn\'t a real EPUB',
		description: 'We couldn\'t open this file as an EPUB, it may be corrupted or saved in a different format with an .epub extension.',
		ids: ['PKG-003', 'PKG-004', 'PKG-008', 'PKG-027'],
	},
	{
		title: 'Invalid EPUB package structure',
		description: 'Some of the files every EPUB needs (like the mimetype or container files) are missing or in the wrong place. '
			+ 'This usually happens when an EPUB is re-zipped by hand.',
		ids: ['PKG-006', 'PKG-007', 'PKG-025', 'RSC-002', 'RSC-003', 'OPF-002'],
	},
	{
		title: 'Missing files or broken links',
		description: 'The book refers to files or locations that don\'t exist inside the EPUB, such as a missing image, chapter or link target.',
		ids: ['RSC-001', 'RSC-007', 'RSC-008', 'RSC-010', 'RSC-011', 'RSC-012', 'RSC-026'],
	},
	{
		title: 'Invalid file names inside the EPUB',
		description: 'Some files inside the EPUB have names with characters that aren\'t allowed.',
		ids: ['PKG-009', 'PKG-010', 'PKG-011', 'PKG-012'],
	},
	{
		title: 'Formatting errors in the book\'s content',
		description: 'Some of the book\'s pages contain invalid or broken markup, which can make them display incorrectly or not at all.',
		ids: ['RSC-005', 'RSC-016', 'RSC-017'],
		prefixes: ['HTM'],
	},
	{
		title: 'Problems with the book\'s metadata or manifest',
		description: 'The book\'s package information (title, identifier or list of files) is incomplete or invalid.',
		prefixes: ['OPF'],
	},
	{
		title: 'Table of contents problems',
		description: 'The book\'s table of contents is missing or points to the wrong places.',
		prefixes: ['NAV', 'NCX'],
	},
	{
		title: 'Image or media problems',
		description: 'Some images or media files are damaged or in an unsupported format.',
		ids: ['PKG-021', 'PKG-022'],
		prefixes: ['MED'],
	},
	{
		title: 'Stylesheet problems',
		description: 'Some of the book\'s stylesheets (CSS) contain errors.',
		prefixes: ['CSS'],
	},
];

/**
 * Generic message
 */
const EPUB_OTHER_CATEGORY: EpubIssueCategory = {
	title: 'Other problems',
	description: 'We found additional problems in this EPUB.',
	showMessages: true,
};

@Injectable({
	providedIn: CoreModule,
})
export class LeadMagnetService {
	public DEFAULT_SHARED_DISCOUNT = 50;

	public readonly leadMagnets: Signal<LeadMagnetResponse[]> = computed(() => Array.from(this.leadMagnetStore().values()));

	public readonly labelToType: Record<string, ILeadMagnet['asset']['type']> = {
		'Full Book': 'FULL_BOOK',
		Chapter: 'CHAPTER',
		Novella: 'NOVELLA',
	};

	public readonly typeToLabel: Record<ILeadMagnet['asset']['type'], string> = {
		FULL_BOOK: 'Full Book',
		CHAPTER: 'Chapter',
		NOVELLA: 'Novella',
	};

	private readonly leadMagnetStore = signal<ReadonlyMap<string, LeadMagnetResponse>>(new Map());

	private epubEngineConfigured = false;

	constructor(
		private scs: ServerConnectionService,
		private ss: SitesService,
	) { }

	public getSharedPrice(siteAdvert: ISiteAdvert): number {
		const { price, leadMagnet } = siteAdvert;

		let sharedDiscountPercent = this.DEFAULT_SHARED_DISCOUNT;

		if (leadMagnet?.sharedDiscountPercentage) {
			sharedDiscountPercent = leadMagnet.sharedDiscountPercentage;
		}

		return price * (1 - sharedDiscountPercent / 100);
	}

	public selectStep5Item(featureId: string): Signal<LeadMagnetStep5 | undefined> {
		return computed(() => {
			const leadMagnet = this.leadMagnetStore().get(featureId);
			if (!leadMagnet) return undefined;
			const { funding, landingPage, asset } = leadMagnet;
			const assetType = this.typeToLabel[asset.type];
			return {
				funding,
				title: landingPage.title,
				author: landingPage.author,
				cover: landingPage.cover,
				description: '', // No description is shown for step 5.
				headline: landingPage.headline,
				tagline: landingPage.tagline,
				details: `Lead Magnet - ${assetType}`,
			};
		});
	}

	public selectCartItem(featureId: string): Signal<LeadMagnetCart | undefined> {
		return computed(() => {
			const leadMagnet = this.leadMagnetStore().get(featureId);
			if (!leadMagnet) return undefined;
			return {
				image: leadMagnet.landingPage.cover,
				title: leadMagnet.landingPage.title,
			};
		});
	}

	isLeadMagnet(siteId: string): boolean {
		return siteId && siteId === this.ss.LEADMAGNET_SITE_ID;
	}

	// Loads and stores the lead magnet for every lead-magnet-site feature in the list that isn't already cached.
	public loadForFeatures(features: LeadMagnetFeature[]): Observable<LeadMagnetResponse[]> {
		const featureIds = features
		// Is the feature a lead magnet
			.filter((feature) => this.isLeadMagnet(this._siteId(feature._site)))
			.map((feature) => feature._id)
		// Is the leadMagnet already cached
			.filter((featureId) => !this.leadMagnetStore().has(featureId));

		if (!featureIds.length) {
			return of([]);
		}

		return forkJoin(featureIds.map((featureId) => this.getByFeatureId(featureId))).pipe(
			map((results) => results.filter((result) => result.success).map((result) => result.leadMagnet)),
		);
	}

	public getByFeatureId(featureId: string): Observable<{ success: boolean; leadMagnet: LeadMagnetResponse }> {
		const cached = this.leadMagnetStore().get(featureId);
		if (cached) {
			return of({ success: true, leadMagnet: cached });
		}
		return this.scs.get$(`/api/lead-magnets/${featureId}`)
			.pipe(tap((res: { success: boolean; leadMagnet: LeadMagnetResponse }) => {
				if (res.success && res.leadMagnet) {
					this._setInStore(res.leadMagnet);
				}
			}));
	}

	public upsert(
		payload: LeadMagnetPayload,
	): Observable<{ success: boolean; leadMagnet: LeadMagnetResponse }> {
		return this.scs.post$('/api/lead-magnets/', payload)
			.pipe(tap((res) => {
				if (res.success) {
					const { leadMagnet } = res;

					if (leadMagnet) {
						this._setInStore(leadMagnet);
					}
				}
			}));
	}

	public uploadCover(file: File): Observable<{ success: boolean; url: string }> {
		return this.scs.post$('/api/lead-magnets/cover', this._fileToFormData(file));
	}

	public uploadAsset(file: File): Observable<{ success: boolean; url: string }> {
		return this.scs.post$('/api/lead-magnets/asset', this._fileToFormData(file));
	}

	public getDownload(featureId: string): string {
		return `/api/lead-magnet-signup/${featureId}/download`;
	}

	public removeAsset(featureId: string): Observable<{success: boolean}> {
		return this.scs.delete$(`/api/lead-magnets/${featureId}/asset`);
	}

	/**
	 * Validates a given epub blob, returns valid if the file is valid or not an epub, returns a set
	 * of human-readable errors.
	 *
	 * Note: This might be moved to the backend in the future for reliability but for now we just
	 * want a simple guard that prevents users from uploading completely corrupted files.
	 *
	 * Validation is provided via:
	 * https://github.com/jlarmstrongiv/epubcheck-standalone/tree/main
	 */
	public async validateAsset(file: File): Promise<ILeadMagnetValidation> {
		/**
		 * Check if it's an epub
		 */
		const isEpub = file.name.toLowerCase().endsWith('.epub') || file.type === 'application/epub+zip';
		if (!isEpub) return { valid: true };

		/**
		 * Validate the file.
		 * TODO: Test with real world files to see if we need a worker thread or not.
		 */
		const [err, result] = await to(this._runEpubCheck(file));
		if (err) {
			// The validator is a convenience guard, don't block the upload if it fails to run.
			// eslint-disable-next-line no-console
			console.error('EPUB validation failed to run', err);
			return { valid: true };
		}
		if (result.valid) return { valid: true };

		/**
		 * Map common error codes to human-readable messages.
		 */
		const errors = result.messages.filter((message) => message.severity === 'ERROR' || message.severity === 'FATAL');
		if (!errors.length) return { valid: true };

		return { valid: false, details: this._groupEpubIssues(errors) };
	}

	private _fileToFormData(file: File): FormData {
		const formData = new FormData();
		formData.append('file', file);
		formData.append('fileName', file.name);
		return formData;
	}

	/**
	 * Lazy loads epubcheck so it stays out of the main bundle, the ~21MB engine itself is served
	 * from the assets folder (see angular.json) and loaded by the library on the first run.
	 */
	private async _runEpubCheck(file: File): Promise<EpubCheckResult> {
		const [{ validate, configureEngine }, { blob }] = await Promise.all([
			import('epubcheck-standalone'),
			import('epubcheck-standalone/plugins'),
		]);

		if (!this.epubEngineConfigured) {
			configureEngine({ url: EPUB_ENGINE_URL });
			this.epubEngineConfigured = true;
		}

		return validate(blob(file), { name: 'book.epub', timeoutMs: EPUB_VALIDATION_TIMEOUT_MS });
	}

	private _groupEpubIssues(messages: ReportMessage[]): ILeadMagnetValidationIssue[] {
		const groups = new Map<EpubIssueCategory, { files: Set<string>; messages: Set<string> }>();

		messages.forEach((message) => {
			const category = this._epubIssueCategory(message.id);
			const group = groups.get(category) ?? { files: new Set<string>(), messages: new Set<string>() };
			const fileLabel = this._epubFileLabel(message);
			if (fileLabel) group.files.add(fileLabel);
			if (category.showMessages) group.messages.add(message.message);
			groups.set(category, group);
		});

		// Keep our category order regardless of the order EPUBCheck emitted the messages in.
		return [...EPUB_ISSUE_CATEGORIES, EPUB_OTHER_CATEGORY]
			.filter((category) => groups.has(category))
			.map((category) => {
				const group = groups.get(category);
				return {
					title: category.title,
					description: category.description,
					files: this._capList([...group.files]),
					messages: this._capList([...group.messages]),
				};
			});
	}

	private _epubIssueCategory(id: string): EpubIssueCategory {
		const exact = EPUB_ISSUE_CATEGORIES.find((category) => category.ids?.includes(id));
		if (exact) return exact;
		const prefix = id.split('-')[0];
		const byPrefix = EPUB_ISSUE_CATEGORIES.find((category) => category.prefixes?.includes(prefix));
		return byPrefix ?? EPUB_OTHER_CATEGORY;
	}

	private _epubFileLabel(message: ReportMessage): string | null {
		// Container level messages are file-wide, those are useless.
		if (!message.path || message.path === './book.epub') return null;
		if (message.line > 0) return `${message.path} (line ${message.line})`;
		return message.path;
	}

	/**
	 * Limit displayed messages (Epubs can be really really busted lol)
	 */
	private _capList(items: string[]): string[] {
		const maxFilesPerIssue = 5;
		if (items.length <= maxFilesPerIssue) return items;
		const remaining = items.length - maxFilesPerIssue;
		return [...items.slice(0, maxFilesPerIssue), `...and ${remaining} more`];
	}

	private _siteId(site: string | { _id: string }): string {
		return typeof site === 'string' ? site : site?._id;
	}

	private _setInStore(leadMagnet: LeadMagnetResponse): void {
		this.leadMagnetStore.update((store) => {
			const next = new Map(store);
			next.set(leadMagnet._feature._id, leadMagnet);
			return next;
		});
	}
}
